import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { caseService, projectService, taskService, searchClientsAsync } from '../../services/api';
import UserSelectDropdown from '../common/UserSelectDropdown';
import AsyncSearchableSelect from '../common/AsyncSearchableSelect';

const INITIAL_TASK_FORM = {
  name: '',
  description: '',
  status: 'todo',
  priority: 'medium',
  estimated_hours: '',
  due_date: '',
  assigned_to: '',
  task_type: 'research'
};

const QuickAddTaskModal = ({ isOpen, onClose, onSuccess }) => {
  const location = useLocation();

  const [cases, setCases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClientLabel, setSelectedClientLabel] = useState(null);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [taskForm, setTaskForm] = useState({ ...INITIAL_TASK_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadingCases, setLoadingCases] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [autoDetected, setAutoDetected] = useState(false);

  // Auto-detect case context from URL
  useEffect(() => {
    if (!isOpen || autoDetected) return;
    const match = location.pathname.match(/^\/cases\/([^/]+)/);
    if (match) {
      const caseId = match[1];
      const loadCaseContext = async () => {
        try {
          const caseRes = await caseService.getCase(caseId);
          const caseData = caseRes.data;
          if (caseData.client_id) {
            setSelectedClientId(caseData.client_id);
            const clientName = caseData.client_name || caseData.client?.first_name
              ? `${caseData.client?.first_name || ''} ${caseData.client?.last_name || ''}`.trim()
              : null;
            setSelectedClientLabel(clientName);
          }
          setSelectedCaseId(caseId);
          setAutoDetected(true);
        } catch (err) {
          console.error('Error loading case context:', err);
        }
      };
      loadCaseContext();
    }
  }, [isOpen, location.pathname, autoDetected]);

  // Load cases when client changes
  useEffect(() => {
    if (!selectedClientId) {
      setCases([]);
      if (!autoDetected) {
        setSelectedCaseId('');
      }
      setProjects([]);
      setSelectedProjectId('');
      return;
    }
    const loadCases = async () => {
      setLoadingCases(true);
      try {
        const res = await caseService.getCasesByClient(selectedClientId);
        setCases(res.data || []);
      } catch (err) {
        console.error('Error loading cases:', err);
        setCases([]);
      } finally {
        setLoadingCases(false);
      }
    };
    loadCases();
  }, [selectedClientId, autoDetected]);

  // Load projects when case changes
  useEffect(() => {
    if (!selectedCaseId) {
      setProjects([]);
      setSelectedProjectId('');
      return;
    }
    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const res = await projectService.getProjectsByCase(selectedCaseId);
        const projectList = res.data || [];
        setProjects(projectList);
        if (projectList.length === 1) {
          setSelectedProjectId(projectList[0].id);
        }
      } catch (err) {
        console.error('Error loading projects:', err);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };
    loadProjects();
  }, [selectedCaseId]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTaskForm({ ...INITIAL_TASK_FORM });
      setError(null);
      setSaving(false);
      setAutoDetected(false);
      setSelectedClientId('');
      setSelectedClientLabel(null);
      setSelectedCaseId('');
      setSelectedProjectId('');
      setCases([]);
      setProjects([]);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProjectId) {
      setError('Please select a client, case, and project');
      return;
    }
    if (!taskForm.name) {
      setError('Task name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const taskData = {
        project_id: selectedProjectId,
        title: taskForm.name,
        description: taskForm.description || null,
        status: taskForm.status || 'todo',
        priority: taskForm.priority || 'medium',
        task_type: taskForm.task_type || 'other',
        estimated_hours: taskForm.estimated_hours ? parseFloat(taskForm.estimated_hours) : null,
        due_date: taskForm.due_date || null,
        assigned_to: taskForm.assigned_to || null,
        billable: true
      };

      Object.keys(taskData).forEach(key => {
        if (taskData[key] === '') {
          taskData[key] = null;
        }
      });

      await taskService.createTask(taskData);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating task:', err);
      let errorMessage = 'Failed to create task';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
        } else {
          errorMessage = err.response.data.detail;
        }
      }
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={handleBackdropClick}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[calc(100vh-120px)] overflow-hidden flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">New Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex-1 overflow-y-auto">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
              <AsyncSearchableSelect
                value={selectedClientId}
                onChange={(val) => {
                  setSelectedClientId(val);
                  setSelectedCaseId('');
                  setSelectedProjectId('');
                }}
                onSearch={searchClientsAsync}
                placeholder="Select a client"
                searchPlaceholder="Search clients..."
                selectedLabel={selectedClientLabel}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Case *</label>
              <select
                value={selectedCaseId}
                onChange={(e) => {
                  setSelectedCaseId(e.target.value);
                  setSelectedProjectId('');
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                disabled={!selectedClientId || loadingCases}
              >
                <option value="">
                  {!selectedClientId ? 'Select a client first' : loadingCases ? 'Loading cases...' : 'Select a case'}
                </option>
                {cases.map(c => (
                  <option key={c.id} value={c.id}>{c.case_number ? `${c.case_number} - ` : ''}{c.title}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                disabled={!selectedCaseId || loadingProjects}
              >
                <option value="">
                  {!selectedCaseId ? 'Select a case first' : loadingProjects ? 'Loading projects...' : projects.length === 0 ? 'No projects found' : 'Select a project'}
                </option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Name *</label>
              <input
                type="text"
                value={taskForm.name}
                onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Enter task name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Task description"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
                <select
                  value={taskForm.task_type}
                  onChange={(e) => setTaskForm({ ...taskForm, task_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="research">Research</option>
                  <option value="drafting">Drafting</option>
                  <option value="filing">Filing</option>
                  <option value="meeting">Meeting</option>
                  <option value="court_appearance">Court Appearance</option>
                  <option value="client_communication">Client Communication</option>
                  <option value="discovery">Discovery</option>
                  <option value="review">Review</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={taskForm.status}
                  onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on_hold">On Hold</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Hours</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={taskForm.estimated_hours}
                  onChange={(e) => setTaskForm({ ...taskForm, estimated_hours: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="0.0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
              <UserSelectDropdown
                value={taskForm.assigned_to}
                onChange={(userId) => setTaskForm({ ...taskForm, assigned_to: userId || '' })}
                placeholder="Search for a team member..."
              />
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Creating...' : 'Create Task'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickAddTaskModal;
